/**
 * WebUpdateBanner
 *
 * Browser/web counterpart to DesktopUpdateBanner. Electron uses
 * electron-updater + the desktop banner; the plain browser web target
 * had no update detection at all until this — users had to manually
 * unregister the service worker to pick up new deploys. The hook
 * (`useWebUpdateMonitor`) watches the SW lifecycle for a new build to
 * land, this component surfaces it and gives the user a one-click
 * reload.
 *
 * Renders nothing on native (iOS/Android) and nothing in Electron
 * (DesktopUpdateBanner has that surface). Renders nothing when no
 * update is detected. Drop it in once near the root and forget — the
 * hook owns the lifecycle, the banner owns the UI.
 */

import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Platform, useWindowDimensions } from 'react-native';
import { useTheme } from '../hooks/useTheme';
import { isElectron, isWeb, DESKTOP_BREAKPOINT } from '../utilities/platform';
import { useWebUpdateMonitor } from '../utilities/useWebUpdateMonitor';
import Icon from './Icon';

// Same geometry constants as DesktopUpdateBanner so the two surfaces
// look identical in the off-chance both ever fire (they can't — Electron
// hides this one — but consistency is cheap).
const WIDGET_GUTTER = 64;
const DESKTOP_TOAST_MAX_WIDTH = 520;

export default function WebUpdateBanner() {
  const theme = useTheme();
  const { width } = useWindowDimensions();
  const { updateAvailable, reload, dismiss } = useWebUpdateMonitor();

  // Web-but-not-electron is the target surface. The hook already
  // short-circuits in those environments, but gate render too so the
  // banner never paints in iOS/Android or Electron even for the brief
  // mount cycle before the hook's effect runs.
  if (!isWeb || isElectron) return null;
  if (!updateAvailable) return null;

  const isDesktopWidth = width >= DESKTOP_BREAKPOINT;

  return (
    <View
      style={[
        styles.container,
        {
          backgroundColor: theme.colors.card,
          borderColor: theme.colors.border,
          bottom: isDesktopWidth ? WIDGET_GUTTER + 8 : 16,
        },
        isDesktopWidth
          ? {
              left: 0,
              right: 0,
              marginLeft: 'auto' as any,
              marginRight: 'auto' as any,
              width: Math.min(DESKTOP_TOAST_MAX_WIDTH, width - 32),
              maxWidth: DESKTOP_TOAST_MAX_WIDTH,
            }
          : { left: 16, right: 16 },
      ]}
    >
      <View style={[styles.iconWrap, { backgroundColor: theme.colors.primary + '18' }]}>
        <Icon icon={['fas', 'arrow-up']} size={14} color={theme.colors.primary} />
      </View>
      <View style={styles.body}>
        <Text style={[styles.title, { color: theme.colors.text }]}>Update available</Text>
        <Text style={[styles.message, { color: theme.colors.muted }]}>
          A new version of Eleplan is ready. Reload to apply.
        </Text>
      </View>
      <TouchableOpacity onPress={dismiss} style={styles.laterBtn} activeOpacity={0.7}>
        <Text style={[styles.laterText, { color: theme.colors.muted }]}>Later</Text>
      </TouchableOpacity>
      <TouchableOpacity
        onPress={reload}
        style={[styles.restartBtn, { backgroundColor: theme.colors.primary }]}
        activeOpacity={0.85}
      >
        <Text style={[styles.restartText, { color: theme.colors.primaryText || '#fff' }]}>Reload</Text>
      </TouchableOpacity>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderRadius: 14,
    borderWidth: StyleSheet.hairlineWidth,
    zIndex: 1000,
    ...Platform.select({
      web: {
        boxShadow: '0 12px 32px rgba(0,0,0,0.18), 0 4px 12px rgba(0,0,0,0.10)',
      } as any,
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 6 },
        shadowOpacity: 0.18,
        shadowRadius: 14,
        elevation: 8,
      },
    }),
  },
  iconWrap: {
    width: 28,
    height: 28,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  body: { flex: 1, minWidth: 0 },
  title: { fontSize: 14, fontWeight: '700', marginBottom: 1 },
  message: { fontSize: 12, lineHeight: 16 },
  laterBtn: { paddingHorizontal: 12, paddingVertical: 8, marginLeft: 8 },
  laterText: { fontSize: 13, fontWeight: '600' },
  restartBtn: { paddingHorizontal: 14, paddingVertical: 8, borderRadius: 8, marginLeft: 4 },
  restartText: { fontSize: 13, fontWeight: '700' },
});
